import React, { useMemo } from 'react';
import './WorkflowDataSelector.scss';

interface DataItem {
    label: string;
    value: string;
    subtext?: string;
    unavailable?: boolean;
    external?: boolean;
}

interface DataSection {
    key: string;
    data: DataItem[];
}

interface TransformedSection {
    key: string;
    data?: DataItem[];
    subdata?: DataSection[];
}

interface WorkflowDataSelectorProps {
    data: DataSection[];
    onDataSelected: (value: string, item: any) => void;
    elementContainerClass?: string;
    searchString?: string;
    labelStyle?: string;
    containerClassName?: string;
    saveIntegrationData?: (data: DataItem[]) => void;
}

const WorkflowDataSelector: React.FC<WorkflowDataSelectorProps> = ({
    data,
    onDataSelected,
    elementContainerClass,
    searchString,
    labelStyle,
    containerClassName,
    saveIntegrationData,
}) => {

    const processedData = useMemo(() => {
        const searchLower = (searchString && searchString.toString().toLowerCase().trim()) || '';
        const hasSearch = searchLower.length > 0;

        // Get filtered data based on search
        const getFilteredData = (): DataSection[] => {
            if (!hasSearch) return data;

            const results: DataSection[] = [];
            const processedKeys = new Set<string>();

            // Process each section
            data.forEach(({ key, data: sectionData }) => {
                const keyMatches = key.toLowerCase().includes(searchLower);
                const matchingItems = sectionData.filter(item =>
                    item.label.toLowerCase().includes(searchLower)
                );

                if (keyMatches) {
                    // If key matches, include all items from this section
                    results.push({ key, data: sectionData });
                    processedKeys.add(key);
                } else if (matchingItems.length > 0) {
                    // If only some items match, include only those items
                    results.push({ key, data: matchingItems });
                    processedKeys.add(key);
                }
            });

            return results;
        };

        // Transform data to separate integrations
        const transformData = (inputData: DataSection[]): TransformedSection[] => {
            const integrationSubdata: DataSection[] = [];
            const finalOutput: TransformedSection[] = [];

            inputData.forEach(({ key, data: sectionData }) => {
                const externalItems = sectionData.filter(item => item.external === true);
                const nonExternalItems = sectionData.filter(item => item.external !== true);

                if (externalItems.length > 0) {
                    integrationSubdata.push({ key, data: externalItems });
                }

                if (nonExternalItems.length > 0) {
                    finalOutput.push({ key, data: nonExternalItems });
                }
            });

            // Add integrations at the beginning if they exist
            if (integrationSubdata.length > 0) {
                finalOutput.unshift({
                    key: 'Integrations',
                    subdata: integrationSubdata
                });
            }

            return finalOutput;
        };

        const filteredData = getFilteredData();
        const transformedData = transformData(filteredData);

        return {
            transformedData,
            hasResults: transformedData.length > 0,
            hasSearch
        };
    }, [data, searchString]);

    // Helper function to get display name for sections
    const getSectionDisplayName = (key: string): string => {
        const nameMap: Record<string, string> = {
            'landing-page': 'Landing Pages',
            'library-content': 'Content Library',
            'direct-message': 'Direct Message'
        };
        return nameMap[key] || key;
    };

    // Helper function to get integration display name
    const getIntegrationDisplayName = (key: string): string => {
        return key === 'kit' ? 'Kit (Convertkit)' : key;
    };

    // Render integration subsection
    const renderIntegrationSubsection = (subsection: DataSection, subIndex: number) => {
        const displayName = getIntegrationDisplayName(subsection.key);
        const containerClass = processedData.hasSearch
            ? 'workflow-data-selector-icon'
            : 'workflow-data-selector-subdata';
        const titleClass = processedData.hasSearch
            ? 'workflow-data-selector-IntegrationTitle workflow-data-selector-IntegrationTitle-search'
            : 'workflow-data-selector-IntegrationTitle';

        return (
            <div key={subIndex} className={containerClass}>
                <p
                    className={titleClass}
                    onClick={() => {
                        if (saveIntegrationData) {
                            saveIntegrationData(subsection.data);
                        }
                        onDataSelected(displayName, {
                            ...subsection,
                            external: true
                        });
                    }}
                >
                    {displayName}
                </p>
            </div>
        );
    };

    // Render regular data items
    const renderDataItems = (sectionData: DataItem[]) => {
        return sectionData.map((dataElement, idx) => (
            <div
                key={idx}
                className={`workflow-data-selector-element-container${elementContainerClass ? ` ${elementContainerClass}` : ''
                    } ${dataElement.unavailable ? 'unavailable' : ''}`}
                onClick={() => onDataSelected(dataElement.value, dataElement)}
            >
                <p
                    className={`workflow-data-selector-element-label ${dataElement.unavailable ? 'unavailable' : ''
                        } ${labelStyle || ''}`}
                >
                    {dataElement.label}
                </p>
                {dataElement.subtext && (
                    <p className="workflow-data-selector-element-subtext">
                        {dataElement.subtext}
                    </p>
                )}
            </div>
        ));
    };

    // Render a section (with or without subdata)
    const renderSection = (section: TransformedSection, index: number) => {
        return (
            <div key={index}>
                {section.subdata ? (
                    // Handle Integration section with nested subdata
                    <>
                        <p className="workflow-data-selector-categoryTitle">{section.key}</p>
                        {section.subdata.map((subsection, subIndex) =>
                            renderIntegrationSubsection(subsection, subIndex)
                        )}
                    </>
                ) : (
                    // Handle normal categories
                    <>
                        <p className="workflow-data-selector-categoryTitle">
                            {getSectionDisplayName(section.key)}
                        </p>
                        {section.data && renderDataItems(section.data)}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className={`workflow-data-selector-container ${containerClassName || ''}`}>
            {processedData.hasResults ? (
                processedData.transformedData.map((section, index) =>
                    renderSection(section, index)
                )
            ) : (
                <h1 className="workflow-data-selector-no-results">No results found</h1>
            )}
        </div>
    );
};

export default WorkflowDataSelector;